import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";

// Square-specific checkout. Loads the Web Payments SDK, renders card
// fields, tokenizes on submit, and returns an opaque `client_data`
// blob the parent posts to the server.
//
// The parent owns the form state (amount, donor info, fees); this
// component owns everything Square-specific.
const SquareCheckout = forwardRef(function SquareCheckout({ clientConfig, totalCents, donorName, donorEmail, onError }, ref) {
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  const [loadError, setLoadError] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!clientConfig?.app_id) return;

    const sdkUrl = clientConfig.environment === "production"
      ? "https://web.squarecdn.com/v1/square.js"
      : "https://sandbox.web.squarecdn.com/v1/square.js";

    const FAIL_MSG = "Could not load the payment form. Please refresh and try again.";

    function handleLoadError() {
      console.error("Square SDK failed to load:", sdkUrl);
      setLoadError(FAIL_MSG);
      onError?.(FAIL_MSG);
    }

    // Already loaded (e.g. a previous mount): init straight away.
    if (window.Square) {
      initCard();
      return;
    }

    // Script tag exists but the SDK hasn't finished loading yet (a
    // concurrent mount injected it). Calling initCard() now would hit a
    // missing window.Square and silently leave the form empty — wait for
    // the in-flight script's load/error instead.
    const existing = document.querySelector(`script[src="${sdkUrl}"]`);
    if (existing) {
      existing.addEventListener("load", initCard);
      existing.addEventListener("error", handleLoadError);
      return () => {
        existing.removeEventListener("load", initCard);
        existing.removeEventListener("error", handleLoadError);
      };
    }

    const script = document.createElement("script");
    script.src = sdkUrl;
    script.onload = initCard;
    script.onerror = handleLoadError;
    document.head.appendChild(script);

    async function initCard() {
      try {
        if (!window.Square) return handleLoadError();
        const payments = window.Square.payments(clientConfig.app_id, clientConfig.location_id);
        const card = await payments.card();
        if (containerRef.current) {
          await card.attach(containerRef.current);
          cardRef.current = card;
          setReady(true);
        }
      } catch (e) {
        console.error("Square card init failed:", e);
        setLoadError(FAIL_MSG);
        onError?.(FAIL_MSG);
      }
    }
  }, [clientConfig?.app_id, clientConfig?.environment, clientConfig?.location_id, onError]);

  // Parent calls this to tokenize + return client_data. Returns
  // null on failure (after calling onError); returns a client_data
  // hash on success that the parent passes to the server.
  useImperativeHandle(ref, () => ({
    async tokenize() {
      if (!cardRef.current) {
        onError?.("Payment form didn't load. Please refresh the page and try again.");
        return null;
      }
      const result = await cardRef.current.tokenize({
        billingContact: {
          email: donorEmail || undefined,
          givenName: donorName?.split(" ")[0] || undefined,
          familyName: donorName?.split(" ").slice(1).join(" ") || undefined,
        },
        // Square SCA verificationDetails — customerInitiated is required.
        // True = the customer (donor) is in the browser actively
        // submitting the charge, not a recurring backend charge.
        customerInitiated: true,
        sellerKeyedIn: false,
        currencyCode: "CAD",
        intent: "CHARGE",
        amount: (totalCents / 100).toFixed(2),
      });
      if (result.status !== "OK") {
        const msg = result.errors?.[0]?.message || "Please check your card details and try again.";
        onError?.(msg);
        return null;
      }
      return { source_id: result.token };
    },
  }), [donorEmail, donorName, totalCents, onError]);

  return (
    <div className="space-y-2">
      {!ready && !loadError && (
        <div className="flex h-12 items-center rounded-lg border border-dashed border-input bg-card px-4 text-sm text-muted-foreground motion-safe:animate-pulse" role="status">
          Loading the secure card form…
        </div>
      )}
      <div ref={containerRef} />
      {loadError && <p className="text-sm text-destructive" role="alert">{loadError}</p>}
    </div>
  );
});

export default SquareCheckout;
